/**
 * Lead labels
 * Dynamic mapping from JSON based on current language
 * Uses getLanguage() to determine which locale to use
 */

import svLeads from '../locales/sv/leads.json';
import enLeads from '../locales/en/leads.json';
import { getLanguage, type Language } from '@/utils/languageUtils';

/**
 * Helper function to get the correct locale based on language
 */
const getLeadsLocale = (lang?: Language) => {
  const currentLang = lang || getLanguage();
  return currentLang === 'en' ? enLeads : svLeads;
};

/**
 * Lead Status labels - dynamically uses current language
 */
export const leadStatusLabels = (lang?: Language): Record<string, string> => {
  const locale = getLeadsLocale(lang);
  return {
    NEW: locale.status.new,
    CONTACTED: locale.status.contacted,
    FOLLOW_UP: locale.status.followUp,
    CONVERTED: locale.status.converted,
    LOST: locale.status.lost,
  };
};

/** Label for "delayed" (lead in status NEW and older than 24h) – not a backend status. */
export const leadDelayedLabel = (lang?: Language): string => {
  const locale = getLeadsLocale(lang);
  return (locale.status as { delayed?: string }).delayed ?? "Försenad";
};

/**
 * Reverse lookup: Label -> LeadStatus (auto-generated from same source)
 * Uses current language to determine which labels to use
 */
export const labelToLeadStatus = (lang?: Language): Record<string, string> => {
  const labels = leadStatusLabels(lang);
  return Object.fromEntries(
    Object.entries(labels).map(([enumKey, label]) => [label, enumKey])
  ) as Record<string, string>;
};

/**
 * Lead UI labels - dynamically uses current language
 */
export const leadUILabels = (lang?: Language): Record<string, string> => {
  const locale = getLeadsLocale(lang);
  return locale.ui;
};

/**
 * Lead Toast labels - dynamically uses current language
 */
export const leadToastLabels = (lang?: Language): Record<string, string> => {
  const locale = getLeadsLocale(lang);
  return locale.toasts;
};
